import i18n from "i18next";

import type {
  DurationUnit,
  Equipment,
  Movement,
  Position,
  PrimaryFunction,
  TargetJoints,
} from "api/services/content/schemas/shared";
import { useGetTreatmentScheduleGroups } from "api/services/treatment/hooks";
import type { Group } from "api/services/treatment/schemas/Treatment";
import {
  translateDurationUnit,
  translateExerciseEquipment,
  translateMovement,
  translatePainLocation,
  translatePosition,
  translatePrimaryFunction,
  translateTemplateGroup,
} from "utils/translations";

type Option<T> = {
  label: string;
  value: T;
};

const DURATION_UNIT_OPTIONS: DurationUnit[] = ["minutes", "repetitions", "seconds", "steps"];
export function useDurationUnitOptions(): Option<DurationUnit>[] {
  return DURATION_UNIT_OPTIONS.map(durationUnit => ({
    label: translateDurationUnit(durationUnit),
    value: durationUnit,
  }));
}

const EQUIPMENT_OPTIONS: Equipment[] = [
  "backpack",
  "exercise_ball",
  "foamroller",
  "mobility_aid",
  "pool",
  "resistance_band",
  "sling",
  "stair",
  "stick",
  "towel",
  "water_bottles",
  "weight",
];
export function useEquipmentOptions(): Option<Equipment>[] {
  return EQUIPMENT_OPTIONS.map(equipment => ({
    label: translateExerciseEquipment(equipment),
    value: equipment,
  }));
}

export function useGroupOptions(): Option<Group>[] {
  const { data } = useGetTreatmentScheduleGroups();
  const groupOptions =
    data?.groups?.map(group => ({ label: translateTemplateGroup(group.key), value: group.key })) || [];

  return groupOptions;
}

const MOVEMENT_OPTIONS: Movement[] = [
  "abduction",
  "adduction",
  "extension",
  "external_rotation",
  "flexion",
  "internal_rotation",
  "lateral_flexion",
];
export function useMovementOptions(): Option<Movement>[] {
  return MOVEMENT_OPTIONS.map(movement => ({
    label: translateMovement(movement),
    value: movement,
  }));
}

const PRIMARY_FUNCTIONS: PrimaryFunction[] = ["balance", "mobility", "strength"];
export function usePrimaryFunctionOptions(): Option<PrimaryFunction>[] {
  return PRIMARY_FUNCTIONS.map(primaryFunction => ({
    label: translatePrimaryFunction(primaryFunction),
    value: primaryFunction,
  }));
}

const POSITION_OPTIONS: Position[] = ["lying", "sitting", "standing"];
export function usePositionOptions(): Option<Position>[] {
  return POSITION_OPTIONS.map(position => ({
    label: translatePosition(position),
    value: position,
  }));
}

export const SETS_OPTIONS = Array.from({ length: 10 }, (_, i) => i + 1);
export function useSetsOptions(): Option<number>[] {
  return SETS_OPTIONS.map(sets => ({
    label: i18n.t("common.sets_icu", { count: sets }),
    value: sets,
  }));
}

const REPS_OPTIONS = ["1", "2", "3", "4", "5", "10", "15", "20", "25", "30"];
export function useRepsOptions(): Option<string>[] {
  return REPS_OPTIONS.map(rep => ({
    label: i18n.t("common.reps_icu", { count: parseInt(rep, 10) }),
    value: rep,
  }));
}

const MINUTES_OPTIONS = ["1", "2", "3", "4", "5", "10", "15", "20", "30", "45", "60"];
export function useMinutesOptions(): Option<string>[] {
  return MINUTES_OPTIONS.map(min => ({
    label: i18n.t("common.minutes_icu", { count: parseInt(min, 10) }),
    value: min,
  }));
}

export const SECONDS_OPTIONS = [10, 20, 30, 45, 60, 90, 120];
export function useSecondsOptions(): Option<string>[] {
  return SECONDS_OPTIONS.map(seconds => ({
    label: i18n.t("common.seconds_icu", { count: seconds }),
    value: seconds.toString(),
  }));
}

const TARGET_JOINT_OPTIONS: TargetJoints[] = [
  "elbow",
  "fall_prevention",
  "foot",
  "hand",
  "hip",
  "jaw",
  "knee",
  "lower_back",
  "lower_leg",
  "neck",
  "neuro",
  "osteoporosis",
  "pelvic_floor",
  "shoulder",
  "upper_back",
  "wrist",
  "chest",
];
export function useTargetJointOptions(): Option<TargetJoints>[] {
  return TARGET_JOINT_OPTIONS.map(joint => ({
    label: translatePainLocation({ painLocation: joint }),
    value: joint,
  }));
}
